import { useState, useEffect, useRef } from 'react';
import { Mic, MicOff, Volume2, VolumeX, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/hooks/use-toast';

interface VoiceInterfaceProps {
  isListening: boolean;
  onToggle: () => void;
  onTranscript: (text: string) => void;
  className?: string;
}

interface VoiceSettings {
  language: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  confidenceThreshold: number;
  autoSpeak: boolean;
  speechRate: number;
  speechPitch: number;
  speechVolume: number;
  voice: string;
}

const defaultSettings: VoiceSettings = {
  language: 'en-US',
  continuous: true,
  interimResults: true,
  maxAlternatives: 1,
  confidenceThreshold: 0.8,
  autoSpeak: true,
  speechRate: 1.0,
  speechPitch: 1.0,
  speechVolume: 1.0,
  voice: 'default'
};

// Voice commands for cricket analytics
const voiceCommands = {
  'show performance': 'Show my performance statistics',
  'compare with': 'Compare my performance with',
  'training plan': 'Generate a training plan for me',
  'what should I focus': 'What should I focus on in training?',
  'how am I improving': 'How am I improving over time?',
  'predict my': 'Predict my performance',
  'top performers': 'Who are the top performers?',
  'my average': 'What is my average?',
  'strike rate': 'Show my strike rate',
  'recent sessions': 'Show my recent training sessions'
};

export function VoiceInterface({ 
  isListening, 
  onToggle, 
  onTranscript, 
  className 
}: VoiceInterfaceProps) {
  const [isSupported, setIsSupported] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [confidence, setConfidence] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<VoiceSettings>(defaultSettings);
  const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const synthRef = useRef<SpeechSynthesis | null>(null);

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const speechSynthesis = window.speechSynthesis;
      
      if (SpeechRecognition && speechSynthesis) {
        setIsSupported(true);
        synthRef.current = speechSynthesis;
        
        // Load available voices
        const loadVoices = () => {
          const voices = speechSynthesis.getVoices();
          setAvailableVoices(voices);
        };
        
        loadVoices();
        speechSynthesis.onvoiceschanged = loadVoices;
        
        // Initialize recognition
        const recognition = new SpeechRecognition();
        recognition.continuous = settings.continuous;
        recognition.interimResults = settings.interimResults;
        recognition.maxAlternatives = settings.maxAlternatives;
        recognition.lang = settings.language;
        
        recognition.onstart = () => {
          setError(null);
          setTranscript('');
          setInterimTranscript('');
        };
        
        recognition.onresult = (event) => {
          let finalTranscript = '';
          let interim = '';
          
          for (let i = 0; i < event.results.length; i++) {
            const result = event.results[i];
            const text = result[0].transcript;
            const conf = result[0].confidence;
            
            if (result.isFinal) {
              if (conf >= settings.confidenceThreshold) {
                finalTranscript += text;
                setConfidence(conf);
              }
            } else {
              interim += text;
            }
          }
          
          if (finalTranscript) {
            const processedTranscript = processVoiceCommand(finalTranscript);
            setTranscript(processedTranscript);
            onTranscript(processedTranscript);
            setInterimTranscript('');
          } else {
            setInterimTranscript(interim);
          }
        };
        
        recognition.onerror = (event) => {
          let errorMessage = 'Speech recognition error';
          
          switch (event.error) {
            case 'no-speech':
              errorMessage = 'No speech detected. Please try again.';
              break;
            case 'audio-capture':
              errorMessage = 'Microphone not available';
              break;
            case 'not-allowed':
              errorMessage = 'Microphone permission denied';
              break;
            case 'network':
              errorMessage = 'Network error occurred';
              break;
            default:
              errorMessage = `Speech recognition error: ${event.error}`;
          }
          
          setError(errorMessage);
          toast({
            title: 'Voice Recognition Error',
            description: errorMessage,
            variant: 'destructive'
          });
        };
        
        recognition.onend = () => {
          if (isListening) {
            // Restart recognition if it should be continuous
            recognition.start();
          }
        };
        
        recognitionRef.current = recognition;
      } else {
        setIsSupported(false);
      }
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [settings, isListening]);

  // Handle listening state changes
  useEffect(() => {
    if (!recognitionRef.current || !isSupported) return;
    
    if (isListening) {
      try {
        recognitionRef.current.start();
      } catch (error) {
        console.error('Failed to start recognition:', error);
        setError('Failed to start voice recognition');
      }
    } else {
      recognitionRef.current.stop();
    }
  }, [isListening, isSupported]);

  const processVoiceCommand = (text: string): string => {
    const lowerText = text.toLowerCase();
    
    // Check for predefined voice commands
    for (const [command, expansion] of Object.entries(voiceCommands)) {
      if (lowerText.includes(command)) {
        return expansion;
      }
    }
    
    // Auto-correct common cricket terms
    let processed = text;
    const corrections = {
      'bating': 'batting',
      'boweling': 'bowling',
      'strike weight': 'strike rate',
      'average weight': 'average',
      'wicket keeper': 'wicketkeeper',
      'boundry': 'boundary',
      'sixer': 'six',
      'forehand': 'front foot',
      'backhand': 'back foot'
    };
    
    for (const [wrong, correct] of Object.entries(corrections)) {
      processed = processed.replace(new RegExp(wrong, 'gi'), correct);
    }
    
    return processed;
  };

  const stopSpeaking = () => {
    if (synthRef.current) {
      synthRef.current.cancel();
      setIsSpeaking(false);
    }
  };

  if (!isSupported) {
    return (
      <Alert className="m-4">
        <AlertDescription>
          Voice features are not supported in your browser. Please use Chrome, Firefox, or Safari.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Voice Recognition Button */}
      <Button
        variant={isListening ? "default" : "outline"}
        size="sm"
        onClick={onToggle}
        className={`relative ${isListening ? 'bg-red-600 hover:bg-red-700' : ''}`}
      >
        {isListening ? (
          <MicOff className="h-4 w-4" />
        ) : (
          <Mic className="h-4 w-4" />
        )}
        
        {isListening && (
          <div className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse" />
        )}
      </Button>

      {/* Speech Controls */}
      <Button
        variant="outline"
        size="sm"
        onClick={isSpeaking ? stopSpeaking : undefined}
        disabled={!isSpeaking}
        className={isSpeaking ? 'bg-blue-50' : ''}
      >
        {isSpeaking ? (
          <VolumeX className="h-4 w-4" />
        ) : (
          <Volume2 className="h-4 w-4" />
        )}
      </Button>

      {/* Voice Settings */}
      <Dialog>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm">
            <Settings className="h-4 w-4" />
          </Button>
        </DialogTrigger>
        
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Voice Settings</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Recognition Settings */}
            <div className="space-y-4">
              <h4 className="font-medium">Speech Recognition</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Language</Label>
                  <Select
                    value={settings.language}
                    onValueChange={(value) => setSettings(prev => ({ ...prev, language: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en-US">English (US)</SelectItem>
                      <SelectItem value="en-GB">English (UK)</SelectItem>
                      <SelectItem value="en-AU">English (AU)</SelectItem>
                      <SelectItem value="en-IN">English (India)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Confidence Threshold</Label>
                  <div className="px-2">
                    <Slider
                      value={[settings.confidenceThreshold * 100]}
                      onValueChange={([value]) => 
                        setSettings(prev => ({ ...prev, confidenceThreshold: value / 100 }))
                      }
                      max={100}
                      min={50}
                      step={5}
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      {Math.round(settings.confidenceThreshold * 100)}%
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <Label>Continuous Listening</Label>
                <Switch
                  checked={settings.continuous}
                  onCheckedChange={(checked) => 
                    setSettings(prev => ({ ...prev, continuous: checked }))
                  }
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label>Show Interim Results</Label>
                <Switch
                  checked={settings.interimResults}
                  onCheckedChange={(checked) => 
                    setSettings(prev => ({ ...prev, interimResults: checked }))
                  }
                />
              </div>
            </div>

            {/* Speech Synthesis Settings */}
            <div className="space-y-4">
              <h4 className="font-medium">Text-to-Speech</h4>
              
              <div className="flex items-center justify-between">
                <Label>Auto-speak Responses</Label>
                <Switch
                  checked={settings.autoSpeak}
                  onCheckedChange={(checked) => 
                    setSettings(prev => ({ ...prev, autoSpeak: checked }))
                  }
                />
              </div>
              
              <div className="space-y-2">
                <Label>Voice</Label>
                <Select
                  value={settings.voice}
                  onValueChange={(value) => setSettings(prev => ({ ...prev, voice: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default</SelectItem>
                    {availableVoices.map(voice => (
                      <SelectItem key={voice.name} value={voice.name}>
                        {voice.name} ({voice.lang})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Rate</Label>
                  <Slider
                    value={[settings.speechRate * 100]}
                    onValueChange={([value]) => 
                      setSettings(prev => ({ ...prev, speechRate: value / 100 }))
                    }
                    max={200}
                    min={50}
                    step={10}
                  />
                  <div className="text-xs text-gray-500 text-center">
                    {settings.speechRate.toFixed(1)}x
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Pitch</Label>
                  <Slider
                    value={[settings.speechPitch * 100]}
                    onValueChange={([value]) => 
                      setSettings(prev => ({ ...prev, speechPitch: value / 100 }))
                    }
                    max={200}
                    min={50}
                    step={10}
                  />
                  <div className="text-xs text-gray-500 text-center">
                    {settings.speechPitch.toFixed(1)}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Volume</Label>
                  <Slider
                    value={[settings.speechVolume * 100]}
                    onValueChange={([value]) => 
                      setSettings(prev => ({ ...prev, speechVolume: value / 100 }))
                    }
                    max={100}
                    min={0}
                    step={10}
                  />
                  <div className="text-xs text-gray-500 text-center">
                    {Math.round(settings.speechVolume * 100)}%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Status Indicators */}
      {(isListening || transcript || error) && (
        <Card className="absolute top-full left-0 mt-2 p-3 min-w-64 z-50 shadow-lg">
          <CardContent className="p-0 space-y-2">
            {isListening && (
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-sm">Listening...</span>
              </div>
            )}
            
            {interimTranscript && (
              <div className="text-sm text-gray-500 italic">
                {interimTranscript}
              </div>
            )}
            
            {transcript && (
              <div className="space-y-1">
                <div className="text-sm font-medium">{transcript}</div>
                {confidence > 0 && (
                  <div className="flex items-center gap-2">
                    <Progress value={confidence * 100} className="h-1 flex-1" />
                    <span className="text-xs text-gray-500">
                      {Math.round(confidence * 100)}%
                    </span>
                  </div>
                )}
              </div>
            )}
            
            {error && (
              <Alert>
                <AlertDescription className="text-sm">{error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}